import { EventEmitter } from 'eventemitter3';
import { isEmpty, trim } from 'lodash-es';

import type { AbortList } from '@iconik-io/core/src/abort_controller/abort_list';
import { ISG_FOLDER_EXTERNAL_ID } from '@iconik-io/core/src/constants/isg';
import type { EventsMap } from '@iconik-io/core/src/events/types';
import type { IconikApiClientInterface } from '@iconik-io/iconik-api/src/client';
import type { ApplyAcls } from '@iconik-io/iconik-api/src/sdk/assets';
import { createCollection } from '@iconik-io/iconik-api/src/sdk/collections';
import {
  type AssetsCollectionElasticSchemaWithIdNullable,
  httpGetCollections,
  httpGetCollectionsByCollectionId,
  httpGetCollectionsByCollectionIdAncestors,
  httpGetCollectionsByCollectionIdContents,
} from '@iconik-io/iconik-api/src/services/assets';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import { CreateCollectionError } from '../errors';
import type { LogEvents } from '../types';

// Represents the root collection to avoid having to check for undefined ids everywhere and to
// be able to cache its contents
const UUID_ZERO = '00000000-0000-0000-0000-000000000000';

export type CollectionEntry = {
  // Id of the collection that the asset should be added to.
  collectionId: string;
  // Path on storage where the files should be uploaded to.
  path: string;
};

const toUnixPath = (path: string): string => path.replace(/\\/g, '/');

type CreateCollectionOptions = {
  applyAcls?: ApplyAcls;
};

export interface CollectionsServiceInterface {
  /**
   * Get a collection at a path relative to a base collection. If this collection already exists
   * then just return it. Otherwise traverse the collection tree starting at the base collection
   * and create any collections needed to satisfy the relative path.
   *
   * It is safe to call this method multiple times with the same relative path. It will cache
   * in flight requests to creating the paths so that we don't accidentally create multiple
   * collections with the same path.
   * @param relativePath Path relative to the collection identified by baseCollectionId
   * @param baseCollectionId Collection the user has selected as upload destination.
   *                         Pass undefined to upload to root.
   * @param abortList List of abort signals to abort all requests
   * @param options Options for when collections are created
   * @returns A Promise that will resolve to a collection at baseCollection/relativePath or undefined
   *          in case no base collection was provided and relativePath is empty. In this case there
   *          is no destination collection to upload to.
   */
  getOrCreateCollection(
    relativePath: string,
    baseCollectionId: string | undefined,
    abortList: AbortList,
    options?: CreateCollectionOptions,
  ): Promise<CollectionEntry | undefined>;

  /**
   * Clears any cached collection requests.
   */
  clearCache(): void;
}

export class CollectionsService
  extends EventEmitter<EventsMap<LogEvents>>
  implements CollectionsServiceInterface
{
  // Maps a pathId, i.e. an id based on a base collection id and a path relative to that base collection,
  // to a Promise returning the collection for that pathId
  #cachedCollections = new Map<string, Promise<CollectionEntry>>();

  #cachedCollectionContents = new Map<
    string,
    Promise<AssetsCollectionElasticSchemaWithIdNullable[]>
  >();

  #cachedCollectionPaths = new Map<string, Promise<string>>();

  constructor(private apiClient: IconikApiClientInterface) {
    super();

    this.#addDefaultCollectionEntries();
  }

  getOrCreateCollection = (
    relativePath: string,
    baseCollectionId: string | undefined,
    abortList: AbortList,
    options?: CreateCollectionOptions,
  ): Promise<CollectionEntry | undefined> => {
    const parentCollectionId = baseCollectionId ?? UUID_ZERO;
    const path = toUnixPath(relativePath);
    this.emit('log', {
      level: 'info',
      message: `Getting or creating collection at path '${path}' relative to ${parentCollectionId}`,
    });
    let collectionPromise = this.#getAncestorPath(
      parentCollectionId,
      abortList,
    ).then((basePath) =>
      this.#getCollection(parentCollectionId, basePath, abortList),
    );

    const pathParts = path.split('/').filter((part) => part.length > 0);
    if (pathParts.length === 0 && baseCollectionId === undefined) {
      return Promise.resolve(undefined);
    }

    if (pathParts.length > 0) {
      collectionPromise = collectionPromise.then((baseCollection) =>
        this.#getOrCreateChildCollection(
          pathParts,
          baseCollection,
          abortList,
          options,
        ),
      );
    }
    return collectionPromise.then((collection) => {
      this.emit('log', {
        level: 'info',
        message: `Destination collection is ${collection.collectionId}, ${collection.path}`,
      });
      return {
        collectionId: collection.collectionId,
        path: trim(collection.path, '/'),
      };
    });
  };

  clearCache = () => {
    this.emit('log', {
      level: 'info',
      message: 'Clearing collection caches',
    });

    this.#cachedCollections.clear();
    this.#cachedCollectionContents.clear();
    this.#cachedCollectionPaths.clear();

    this.#addDefaultCollectionEntries();
  };

  #addDefaultCollectionEntries = () => {
    this.#cachedCollections.set(
      UUID_ZERO,
      Promise.resolve({ collectionId: UUID_ZERO, path: '' }),
    );
    this.#cachedCollectionPaths.set(UUID_ZERO, Promise.resolve(''));
  };

  // The method that does most of the work to create a collection path. For each part of
  // a path it will first check if the requested collection exists relative to the base collection.
  // If it doesn't then create it and continue recursing down until all path components have been
  // satisfied.
  #getOrCreateChildCollection = (
    pathParts: string[],
    parentCollectionEntry: CollectionEntry,
    abortList: AbortList,
    options?: CreateCollectionOptions,
  ): Promise<CollectionEntry> => {
    const childName = pathParts[0];
    const path = `${parentCollectionEntry.path}/${childName}`;

    this.emit('log', {
      level: 'debug',
      message: `Checking if collection ${path} exists`,
    });
    let childCollection = this.#getCachedCollection(path);

    if (childCollection === undefined) {
      this.emit('log', {
        level: 'debug',
        message: `Collection ${path} not in cache`,
      });
      childCollection = this.#getCollectionContents(
        parentCollectionEntry.collectionId,
        abortList,
      ).then((children): Promise<CollectionEntry> => {
        const existingChild = children.find(
          (child) => child.title === childName,
        );
        if (existingChild) {
          this.emit('log', {
            level: 'debug',
            message: `Found existing child collection for ${path} in ${parentCollectionEntry.collectionId}`,
          });
          return Promise.resolve({
            collectionId: existingChild.id,
            path,
          });
        }
        return this.#createCollection(
          childName,
          parentCollectionEntry,
          abortList,
          options,
        );
      });
      this.#cachedCollections.set(path, childCollection);
    }

    if (pathParts.length === 1) {
      return childCollection;
    }

    return childCollection.then((parentCollection) =>
      this.#getOrCreateChildCollection(
        pathParts.slice(1),
        {
          collectionId: parentCollection.collectionId,
          path: parentCollection.path,
        },
        abortList,
        options,
      ),
    );
  };

  #getCachedCollection = (path: string): Promise<CollectionEntry> | undefined =>
    this.#cachedCollections.get(path);

  #getCollection = (
    collectionId: string,
    parentPath: string,
    abortList: AbortList,
  ): Promise<CollectionEntry> => {
    const existing = this.#cachedCollections.get(collectionId);
    if (existing) {
      return existing;
    }

    const collection = this.apiClient
      .request(httpGetCollectionsByCollectionId(collectionId), {
        abortSignal: abortList.newSignal(),
      })
      .then(({ body }): CollectionEntry => {
        let path;
        if (body.storage_id && isEmpty(body.parents)) {
          // Covers the case where the result is the root collection for a mapped to storage.
          // In this case it should not be included in the path since we would then get
          // the title of the storage included in the path.
          this.emit('log', {
            level: 'debug',
            message:
              'Target collection is root collection of mapped storage. The title of this collection is not included in path.',
          });
          path = '';
        } else {
          path =
            parentPath.length > 0 ? `${parentPath}/${body.title}` : body.title;
        }
        return {
          collectionId: body.id,
          path,
        };
      })
      .catch((error) => {
        this.#cachedCollections.delete(collectionId);
        throw new CreateCollectionError(error);
      });
    this.#cachedCollections.set(collectionId, collection);
    return collection;
  };

  /**
   * Recursively fetch all the sub collections of a collection.
   */
  #fetchCollectionContents(
    collectionId: string,
    page: number | undefined,
    apiClient: IconikApiClientInterface,
    abortList: AbortList,
  ): Promise<AssetsCollectionElasticSchemaWithIdNullable[]> {
    let request;
    // Uploading to top level so fetch all root collections as contents
    if (collectionId === UUID_ZERO) {
      request = httpGetCollections({
        per_page: 1000,
        is_root: 'true',
        status: 'ACTIVE',
        page,
      });
    } else {
      request = httpGetCollectionsByCollectionIdContents(collectionId, {
        per_page: 1000,
        object_types: 'collections',
        page,
      });
    }
    return apiClient
      .request(request, {
        abortSignal: abortList.newSignal(),
      })
      .then((response) => {
        const { page: responsePage, pages } = response.body;
        const objects = response.body.objects;
        if (responsePage >= pages) {
          return Promise.resolve(objects);
        }
        return this.#fetchCollectionContents(
          collectionId,
          responsePage + 1,
          apiClient,
          abortList,
        ).then((nextPageObjects) => objects.concat(nextPageObjects));
      });
  }

  // Get all sub collections of a collection. This also caches previously fetched collection content.
  #getCollectionContents = (
    collectionId: string,
    abortList: AbortList,
  ): Promise<AssetsCollectionElasticSchemaWithIdNullable[]> => {
    const existing = this.#cachedCollectionContents.get(collectionId);
    if (existing) {
      return existing;
    }
    const collectionContents = this.#fetchCollectionContents(
      collectionId,
      undefined,
      this.apiClient,
      abortList,
    ).catch((error) => {
      this.#cachedCollectionContents.delete(collectionId);
      throw new CreateCollectionError(error);
    });
    this.#cachedCollectionContents.set(collectionId, collectionContents);
    return collectionContents;
  };

  #createCollection = (
    collectionTitle: string,
    parentCollection: CollectionEntry,
    abortList: AbortList,
    options?: CreateCollectionOptions,
  ): Promise<CollectionEntry> => {
    // Clear out the cache for collection contents since it is now invalid
    this.#cachedCollectionContents.delete(parentCollection.collectionId);

    this.emit('log', {
      level: 'info',
      message: `Creating collection ${collectionTitle} in ${parentCollection.collectionId}`,
    });

    const path = `${parentCollection.path}/${collectionTitle}`;
    const parentId =
      parentCollection.collectionId !== UUID_ZERO
        ? parentCollection.collectionId
        : undefined;
    const collectionEntry = unwrapResponseBody(
      createCollection({
        client: this.apiClient,
        params: {
          title: collectionTitle,
          parentId,
          applyAcls: options?.applyAcls,
        },
        abortSignal: abortList.newSignal(),
      }),
    )
      .then(
        (collection): CollectionEntry => ({
          collectionId: collection.id,
          path,
        }),
      )
      .catch((error) => {
        this.#cachedCollections.delete(path);
        throw new CreateCollectionError(error);
      });
    this.#cachedCollections.set(path, collectionEntry);
    return collectionEntry;
  };

  #getAncestorPath = (baseCollectionId: string, abortList: AbortList) => {
    const existing = this.#cachedCollectionPaths.get(baseCollectionId);
    if (existing) {
      return existing;
    }

    const basePath = this.apiClient
      .request(httpGetCollectionsByCollectionIdAncestors(baseCollectionId), {
        abortSignal: abortList.newSignal(),
      })
      .then(({ body }) => {
        let parents = [...body.objects];
        const isStorageMapped = parents.some(({ storage_id }) =>
          Boolean(storage_id),
        );
        if (isStorageMapped) {
          const isISG = parents[0].external_id === ISG_FOLDER_EXTERNAL_ID;
          if (isISG) {
            this.emit('log', {
              level: 'debug',
              message:
                'Parent is ISG mapped collection. Striping first two parts of path',
            });
            parents = parents.slice(2);
          } else {
            this.emit('log', {
              level: 'debug',
              message:
                'Parent is storage mapped collection. Striping first part of path',
            });
            parents = parents.slice(1);
          }
        }
        const path = parents.map(({ title }) => title).join('/');
        this.emit('log', {
          level: 'info',
          message: `Ancestor path for ${baseCollectionId} is ${path}`,
        });
        return path;
      })
      .catch((error) => {
        this.#cachedCollectionPaths.delete(baseCollectionId);
        throw new CreateCollectionError(error);
      });

    this.#cachedCollectionPaths.set(baseCollectionId, basePath);
    return basePath;
  };
}
